/**
 * Parse a preview-thumbnail page — the `?p=N` variants of the gallery
 * detail page — into `[pageNumber, ptoken]` tuples.
 *
 * The site renders previews in two layouts:
 *
 *   - "Normal" (small thumbs): each thumb is a `<div class="gdtm">` with
 *     an inline `style="background:url(spritesheet.jpg)..."` and a child
 *     `<a href="/s/{ptoken}/{gid}-{page}">`. Page number lives in the
 *     anchor's inner text or in the `<a>`'s `title=` attribute.
 *
 *   - "Large" (full thumbs): each thumb is `<div class="gdtl">` with a
 *     direct `<a href="..."><img></a>`.
 *
 * The plan calls out three fallbacks in `image-flow.ts`; this parser handles
 * the "scrape preview thumbnails" step.
 */

import { absoluteUrl, parseDocument } from '@/lib/html';
import { ParseError } from '@/lib/errors';
import type { PagePreviewImage } from '@/sources/types';

const PREVIEW_HREF = /\/s\/([0-9a-z]+)\/(\d+)-(\d+)/i;
const BLANK_THUMB_RE = /\/g\/blank\.gif(?:[?#].*)?$/i;
const CSS_URL_RE = /url\((['"]?)(.*?)\1\)/i;
const CSS_OFFSET_RE =
  /url\([^)]*\)\s*(-?\d+(?:\.\d+)?)(?:px)?\s+(-?\d+(?:\.\d+)?)(?:px)?/i;
const CSS_DIMENSION_RE = /(?:^|;)\s*(width|height)\s*:\s*(\d+(?:\.\d+)?)px/i;

export interface ParsedPagePreview {
  page: number;
  ptoken: string;
  label?: string;
  thumbnail: PagePreviewImage;
}

export interface PreviewParseResult {
  /** `[pageNumber, ptoken]` tuples discovered on this page. */
  entries: Array<[number, string]>;
  /** Thumbnail metadata discovered on this preview page. */
  previews: ParsedPagePreview[];
  /**
   * Total number of preview pages, if the pagination footer is visible.
   * Used by `image-flow.ts` to know when to stop iterating `?p=N`.
   */
  totalPreviewPages?: number;
  /**
   * Gallery's total page count, parsed from the `#gdd` "Length: N pages" row.
   * Same number that `gdata`'s `filecount` returns; available as a side-effect
   * of scraping the first preview page so `image-flow.ts` can size the
   * preview loop without a separate `api.php` call. The pagination footer
   * `totalPreviewPages` is the more direct signal but isn't always reliably
   * parseable (e.g. layout changes); having `galleryPageCount` as a second
   * source lets the loop derive `ceil(pageCount / thumbsOnPage0)` as a
   * fallback.
   */
  galleryPageCount?: number;
}

function parsePreviewAnchor(a: HTMLAnchorElement): { page: number; ptoken: string } | undefined {
  const href = a.getAttribute('href') ?? '';
  const m = PREVIEW_HREF.exec(href);
  if (!m) return undefined;
  const ptoken = m[1];
  const pageStr = m[3];
  if (!ptoken || !pageStr) return undefined;
  const page = Number.parseInt(pageStr, 10);
  if (!Number.isFinite(page) || page <= 0) return undefined;
  return { page, ptoken };
}

function labelForAnchor(a: HTMLAnchorElement): string | undefined {
  const img = a.querySelector('img') as HTMLImageElement | null;
  const label =
    img?.getAttribute('title')?.trim() ||
    img?.getAttribute('alt')?.trim() ||
    a.querySelector('[title]')?.getAttribute('title')?.trim() ||
    a.getAttribute('title')?.trim() ||
    a.textContent?.trim();
  return label ? label : undefined;
}

function numericStyleValue(style: string, name: 'width' | 'height'): number | undefined {
  for (const declaration of style.split(';')) {
    const m = CSS_DIMENSION_RE.exec(declaration);
    if (m?.[1]?.toLowerCase() !== name || !m[2]) continue;
    const value = Number.parseFloat(m[2]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return undefined;
}

function directImageThumbnail(a: HTMLAnchorElement, pageUrl: string): PagePreviewImage | undefined {
  const img = a.querySelector('img') as HTMLImageElement | null;
  const src = img?.getAttribute('src')?.trim();
  if (!src || BLANK_THUMB_RE.test(src)) return undefined;
  const thumbnail: PagePreviewImage = {
    kind: 'image',
    url: absoluteUrl(src, pageUrl),
  };
  const width = Number.parseInt(img?.getAttribute('width') ?? '', 10);
  const height = Number.parseInt(img?.getAttribute('height') ?? '', 10);
  if (Number.isFinite(width) && width > 0) thumbnail.width = width;
  if (Number.isFinite(height) && height > 0) thumbnail.height = height;
  return thumbnail;
}

function spriteThumbnail(a: HTMLAnchorElement, pageUrl: string): PagePreviewImage | undefined {
  const candidates: Element[] = [a, ...Array.from(a.querySelectorAll('[style]'))];
  let parent = a.parentElement;
  while (parent) {
    candidates.push(parent);
    parent = parent.parentElement;
  }

  for (const el of candidates) {
    const style = el.getAttribute('style') ?? '';
    const urlMatch = CSS_URL_RE.exec(style);
    const rawUrl = urlMatch?.[2]?.trim();
    if (rawUrl && !BLANK_THUMB_RE.test(rawUrl)) {
      const offsetMatch = CSS_OFFSET_RE.exec(style);
      const width = numericStyleValue(style, 'width');
      const height = numericStyleValue(style, 'height');
      if (!offsetMatch?.[1] || !offsetMatch[2] || width === undefined || height === undefined) {
        return undefined;
      }
      const x = Math.abs(Number.parseFloat(offsetMatch[1]));
      const y = Math.abs(Number.parseFloat(offsetMatch[2]));
      if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
      return {
        kind: 'sprite',
        url: absoluteUrl(rawUrl, pageUrl),
        x,
        y,
        width,
        height,
      };
    }
  }
  return undefined;
}

function thumbnailForAnchor(a: HTMLAnchorElement, pageUrl: string): PagePreviewImage | undefined {
  return directImageThumbnail(a, pageUrl) ?? spriteThumbnail(a, pageUrl);
}

export function parsePreviewPage(html: string, ctx: { pageUrl?: string } = {}): PreviewParseResult {
  if (!html) throw new ParseError('parsePreviewPage: empty html');
  const doc = parseDocument(html);
  const pageUrl = ctx.pageUrl ?? 'https://e-hentai.org/';

  const entries: Array<[number, string]> = [];
  const previews: ParsedPagePreview[] = [];
  const anchors = Array.from(
    doc.querySelectorAll(
      '#gdt a[href*="/s/"], div.gdtm a[href*="/s/"], div.gdtl a[href*="/s/"], a[href*="/s/"]',
    ),
  ) as HTMLAnchorElement[];

  const seen = new Set<number>();
  for (const a of anchors) {
    const parsed = parsePreviewAnchor(a);
    if (!parsed) continue;
    const { page, ptoken } = parsed;
    if (seen.has(page)) continue;
    seen.add(page);
    entries.push([page, ptoken]);
    const thumbnail = thumbnailForAnchor(a, pageUrl);
    if (thumbnail) {
      const preview: ParsedPagePreview = { page, ptoken, thumbnail };
      const label = labelForAnchor(a);
      if (label !== undefined) preview.label = label;
      previews.push(preview);
    }
  }

  let totalPreviewPages: number | undefined;
  // Pagination: `<table class="ptt">` with the last `<td>` containing a `<a>`
  // whose text is the highest page number.
  const ptt = doc.querySelector('table.ptt');
  if (ptt) {
    const tds = Array.from(ptt.querySelectorAll('td'));
    let max = 1;
    for (const td of tds) {
      const t = td.textContent?.trim() ?? '';
      const n = Number.parseInt(t, 10);
      if (Number.isFinite(n) && n > max) max = n;
    }
    totalPreviewPages = max;
  }

  // Gallery `Length: N pages` lives in the `#gdd` table; it's present on
  // every `?p=N` response. Pulling it here means image-flow.ts can size the
  // preview loop even when the pagination footer parse above misses.
  let galleryPageCount: number | undefined;
  const gddRows = Array.from(doc.querySelectorAll('#gdd tr'));
  for (const row of gddRows) {
    const tds = row.querySelectorAll('td');
    if (tds.length < 2) continue;
    const left = (tds[0]?.textContent ?? '').toLowerCase().replace(/:$/, '').trim();
    const right = (tds[1]?.textContent ?? '').trim();
    if (left === 'length') {
      const m = right.match(/\d[\d,]*/);
      if (m) {
        const n = Number.parseInt(m[0].replace(/,/g, ''), 10);
        if (Number.isFinite(n) && n > 0) galleryPageCount = n;
      }
      break;
    }
  }

  const result: PreviewParseResult = { entries, previews };
  if (totalPreviewPages !== undefined) result.totalPreviewPages = totalPreviewPages;
  if (galleryPageCount !== undefined) result.galleryPageCount = galleryPageCount;
  return result;
}
